import endpointConfig from '@/configs/endpoint.config'
import refreshAccessToken from './refreshAccessToken'
import { clearSession, tokenStorage } from '@/store/authStore'
import { useBillingStore } from '@/store/billingStore'
import type { QuotaError } from '@/@types/trackheros'
import type {
    AxiosError,
    AxiosInstance,
    InternalAxiosRequestConfig,
} from 'axios'

/**
 * Transparent access-token refresh.
 *
 * Access tokens are short-lived by design (backend SIMPLE_JWT), so a normal
 * session hits 401 routinely. Nothing above this layer should have to know
 * that: a 401 is caught here, the token is refreshed, and the original request
 * is replayed. Callers only ever see the eventual result.
 *
 * Three failure modes this has to get right, all of which are user-visible:
 *
 *  1. **Concurrent 401s.** A dashboard page fires several requests at once and
 *     they all 401 together. Refreshing per request would fire N refreshes; the
 *     first rotates the refresh token and blacklists it, so the other N-1 fail
 *     and sign the user out mid-session. One in-flight refresh is shared by all
 *     waiters — and since the chat stream (fetch, not axios) can 401 at the
 *     same moment, the shared promise lives in `refreshAccessToken.ts`, used
 *     by both transports.
 *  2. **Refresh looping.** If the refresh call itself 401s we must not try to
 *     refresh the refresh. It is excluded by URL, and every replay is marked so
 *     it is only ever retried once.
 *  3. **A dead refresh token.** Nothing can be recovered, so the session is
 *     cleared. ProtectedRoute reacts to the store change and redirects — this
 *     layer deliberately does not navigate, so it stays usable outside a router.
 */

type RetriableRequest = InternalAxiosRequestConfig & { _retry?: boolean }

const QUOTA_CODES = new Set<string>([
    'quota_exceeded',
    'storage_cap_exceeded',
    'payment_method_required',
    'feature_not_in_plan',
    'not_a_company_account',
])

const AxiosResponseIntrceptorErrorCallback = async (
    error: AxiosError,
    axiosInstance: AxiosInstance,
) => {
    const { response, config } = error
    const request = config as RetriableRequest | undefined

    // A quota refusal (DEV-87). Published so a top-level dialog can explain it
    // without every call site learning about plans — but the promise is still
    // rejected below, deliberately: this is not a global redirect, and the
    // client-create form needs the error to show inline next to the field.
    //
    // Plan refusals only, by code. A purchase declined at the card (plans v2,
    // `payment_declined`) is also a 402, and explaining it as "your plan limit
    // is reached" would be wrong — the add-ons card says what happened.
    if (response?.status === 402) {
        const payload = response.data as QuotaError | undefined
        if (payload?.code && QUOTA_CODES.has(payload.code)) {
            useBillingStore.getState().showQuotaError(payload)
        }
    }

    const isAuthEndpoint =
        request?.url === endpointConfig.refresh ||
        request?.url === endpointConfig.signIn ||
        request?.url === endpointConfig.signUp

    const shouldRefresh =
        response?.status === 401 &&
        request &&
        !request._retry &&
        !isAuthEndpoint &&
        Boolean(tokenStorage.refresh)

    if (!shouldRefresh) {
        // A 401 we cannot recover from ends the session. Sign-in and sign-up
        // are excluded: a rejected password is a form error, not an expiry.
        if (response?.status === 401 && !isAuthEndpoint) {
            clearSession()
        }
        return Promise.reject(error)
    }

    request._retry = true

    try {
        const accessToken = await refreshAccessToken()
        request.headers.Authorization = `Bearer ${accessToken}`
        return await axiosInstance(request)
    } catch (refreshError) {
        clearSession()
        return Promise.reject(refreshError)
    }
}

export default AxiosResponseIntrceptorErrorCallback
